import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import CardSkeleton from './CardSkeleton'
import ConstrainedWrapper from './ConstrainedWrapper'

interface ListPageSkeletonProps {
  rows?: number
  variant?: 'table' | 'cards' | 'auto'
  columns?: number
}

export default function ListPageSkeleton({
  rows = 8,
  variant = 'auto',
  columns = 5,
}: ListPageSkeletonProps) {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const showTable = variant === 'table' || (variant === 'auto' && isDesktop)

  return (
    <Box sx={{ pb: 10 }} role="status" aria-label="Loading">
      <ConstrainedWrapper maxWidth={2100}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            pt: 4,
            pb: 1,
            flexWrap: 'wrap',
          }}
        >
          <Skeleton
            variant="rounded"
            width={112}
            height={40}
            sx={{ borderRadius: '12px' }}
          />
          <Skeleton
            variant="rounded"
            height={40}
            sx={{
              borderRadius: '12px',
              ml: { sm: 'auto' },
              flex: { xs: 1, sm: 'none' },
              width: { sm: 280 },
            }}
          />
          {isDesktop && (
            <Skeleton
              variant="rounded"
              width={96}
              height={40}
              sx={{ borderRadius: '12px' }}
            />
          )}
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
      </ConstrainedWrapper>

      <ConstrainedWrapper maxWidth={isDesktop ? 1100 : 600}>
        {showTable ? (
          <TableBodySkeleton rows={rows} columns={columns} />
        ) : (
          <Box
            sx={{
              px: 2,
              pt: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {Array.from({ length: rows }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </Box>
        )}
      </ConstrainedWrapper>
    </Box>
  )
}

function TableBodySkeleton({
  rows,
  columns,
}: {
  rows: number
  columns: number
}) {
  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: 2,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#F8FAFC',
          }}
        >
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} variant="text" width="60%" height={18} />
          ))}
        </Box>
        {Array.from({ length: rows }).map((_, r) => (
          <Box
            key={r}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gap: 2,
              px: 2,
              py: 1.5,
              borderBottom: r === rows - 1 ? 'none' : '1px solid',
              borderColor: 'divider',
              alignItems: 'center',
            }}
          >
            {Array.from({ length: columns }).map((_, c) => (
              <Skeleton
                key={c}
                variant="text"
                width={c === 0 ? '80%' : `${40 + ((r + c) % 4) * 15}%`}
                height={20}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  )
}
